/** Stateful fake API for the concept gallery. Reuses the pure rules engine. */
import type { AttackRequest, AttackResponse, ChatMessageResponse, FortifyRequest, GameResponse, GameState as WireState, ReinforceRequest } from '@warcarp/api-types'
import { armyHeadroom, canPlaceOn, isValidSet } from '@warcarp/api-types'
import { applyPlacementExit, applyReinforce, reinforcementCount } from '../../../api/src/engine/reinforce'
import { advancePhase, canAttack, canFortify, canReturnToAttack, returnToAttack } from '../../../api/src/engine/turn'
import { applyBlitzAttack, applyPostConquestTransfer } from '../../../api/src/engine/combat'
import { applyFortify } from '../../../api/src/engine/fortify'
import { buildInitialDeck, applyCardTurnIn } from '../../../api/src/engine/cards'
import { createRng } from '../../../api/src/engine/dice'
import type { GameState } from '../../../api/src/engine/types'
import { getBundledMap } from '../../lib/bundledMaps'
import { gameForCase, visualResponse, visualUser, VISUAL_NOW, type VisualCase } from './transport'

export interface InteractiveGameSnapshot { game: GameResponse; messages: ChatMessageResponse[]; version: number }
export type PreviewApiResponse = { status?: number; body: unknown }
class InvalidMove extends Error {}
function requireMove(allowed: boolean, message = 'Invalid move') { if (!allowed) throw new InvalidMove(message) }

export function createInteractiveGame(scenario: VisualCase, saved?: InteractiveGameSnapshot, onChange?: (snapshot: InteractiveGameSnapshot) => void) {
  const user = { ...visualUser, username: 'amlut16', displayName: 'amlut16' }
  const base = saved?.game ?? gameForCase({ ...scenario, data: 'placement' })
  base.players[0] = { ...base.players[0]!, displayName: user.displayName }
  const wire = base.state as unknown as WireState
  const staticMap = getBundledMap(base.mapId ?? 'minnesota')!
  const byId = new Map((wire.territories ?? []).map(territory => [territory.id, territory]))
  const map = { ...staticMap, territories: staticMap.territories.map((territory, index) => ({
    ...territory,
    ...(saved ? byId.get(territory.id) : {
      ownerId: territory.id === 'cook' || territory.id === 'saint-louis' ? '1' : territory.id === 'lake' ? '2' : String(index % 4 + 1),
      armies: territory.id === 'cook' ? 8 : territory.id === 'lake' ? 2 : 3,
    }),
  })) }
  const deck = buildInitialDeck(map, createRng('42'))
  let state: GameState = saved ? { ...wire, map } as unknown as GameState : {
    ...wire, map, phase: 'placement', turnIndex: wire.players.indexOf('1'),
    rngSeed: '42', attacksThisTurn: 0, fortifiesThisTurn: 0, roundsPlayed: 0,
    bonusArmies: {}, armiesToPlace: Object.fromEntries(wire.players.map(player => [player, 0])),
    lastConquest: null, mustTurnInCards: false, conqueredThisTurn: false,
    cards: Object.fromEntries(wire.players.map(player => [player, player === '1' ? deck.slice(0, 2) : []])),
    deck: deck.slice(2), discard: [], cardTurnIns: 0,
    initialTerritories: map.territories.map(({ id, ownerId, armies }) => ({ id, ownerId, armies })),
    events: [{ kind: 'turn-start', player: '1', turnNumber: 1, ts: VISUAL_NOW - 1000 }],
  } as unknown as GameState
  let version = saved?.version ?? 1
  const messages: ChatMessageResponse[] = saved?.messages ?? [{
    id: 'concept-welcome', gameId: base.id, userId: base.players[1]!.userId, displayName: base.players[1]!.displayName,
    content: 'Your move! Cook borders Lake; Saint Louis is yours for a fortify after the attack.',
    createdAt: new Date(VISUAL_NOW - 2000).toISOString(), reactions: [],
  }]
  function game(): GameResponse {
    const { map: _map, ...rest } = state
    const current = state.players[state.turnIndex]
    return structuredClone({
      ...base, currentPlayerIndex: base.players.findIndex(player => String(player.slot) === current),
      currentTurn: state.roundsPlayed * state.players.length + state.turnIndex,
      state: { ...rest, mapId: map.id, territories: state.map.territories.map(({ id, ownerId, armies }) => ({ id, ownerId, armies })),
        eventCount: state.events?.length ?? 0, stateVersion: version },
    }) as GameResponse
  }
  const snapshot = (): InteractiveGameSnapshot => ({ game: game(), messages: structuredClone(messages), version })
  const changed = () => { version += 1; onChange?.(snapshot()) }

  function respond(pathname: string, method: string, bodyText?: string): PreviewApiResponse | null {
    const path = pathname.split('?')[0]!
    if (method === 'GET') {
      if (path === '/api/v1/auth/me') return { body: user }
      if (path === `/api/v1/games/${base.id}`) return { body: game() }
      if (path === '/api/v1/games') return { body: [game()] }
      if (path === `/api/v1/games/${base.id}/events`) return { body: { events: structuredClone(state.events ?? []), eventCount: state.events?.length ?? 0, since: 0 } }
      if (path === `/api/v1/games/${base.id}/chat`) return { body: structuredClone(messages) }
      return visualResponse(scenario, pathname, method)
    }
    if (method !== 'POST') return null
    let body: Record<string, unknown>
    try { body = JSON.parse(bodyText ?? '{}') as Record<string, unknown> }
    catch { return { status: 400, body: { message: 'Invalid request body' } } }
    try {
      if (path === `/api/v1/games/${base.id}/chat`) {
        requireMove(typeof body.content === 'string' && body.content.trim().length > 0 && body.content.length <= 500, 'Enter a message')
        const message: ChatMessageResponse = { id: `concept-chat-${version}`, gameId: base.id, userId: user.id, displayName: user.displayName,
          content: String(body.content).trim(), createdAt: new Date(VISUAL_NOW + version).toISOString(), reactions: [] }
        messages.push(message)
        changed()
        return { body: message }
      }
      if (!path.startsWith(`/api/v1/games/${base.id}/turn/`)) return visualResponse(scenario, pathname, method)
      requireMove(state.players[state.turnIndex] === '1', 'This turn has ended. Restart the sample to play again.')
      const action = path.split('/').at(-1)
      if (action === 'reinforce') {
        requireMove(state.phase === 'placement', 'Place armies at the start of your turn')
        const placements = (body as unknown as ReinforceRequest).placements
        requireMove(Array.isArray(placements) && placements.length > 0, 'Choose where to place your armies')
        requireMove(new Set(placements.map(placement => placement.territoryId)).size === placements.length, 'Duplicate territory')
        for (const placement of placements) {
          const territory = state.map.territories.find(item => item.id === placement.territoryId)
          requireMove(!!territory && canPlaceOn(state, '1', territory) && Number.isInteger(placement.armies) && placement.armies > 0 && placement.armies <= armyHeadroom(territory), 'Invalid placement')
        }
        requireMove(placements.reduce((total, placement) => total + placement.armies, 0) === reinforcementCount(state, '1'), 'Place all your reinforcements before committing')
        state = applyPlacementExit(advancePhase(applyReinforce(state, '1', placements)), '1')
      } else if (action === 'advance') {
        state = state.phase === 'placement' ? applyPlacementExit(advancePhase(state), '1') : advancePhase(state)
      } else if (action === 'attack') {
        const move = body as unknown as AttackRequest
        const decision = canAttack(state, move)
        requireMove(decision.allowed, decision.reason)
        const result = applyBlitzAttack(state, move)
        requireMove(result.iterations > 0, 'Continuous attack stopped at its army floor')
        state = result.state
        changed()
        const { state: _state, ...combat } = result
        const response: AttackResponse = { ...combat, roll: result.rolls.at(-1)!, game: game() }
        return { body: response }
      } else if (action === 'transfer') {
        requireMove(state.phase === 'attack' && !!state.lastConquest, 'No conquest needs a transfer')
        const source = state.map.territories.find(territory => territory.id === state.lastConquest!.fromTerritoryId)!
        requireMove(Number.isInteger(body.armies) && Number(body.armies) >= 0 && Number(body.armies) < source.armies, 'Invalid transfer')
        state = applyPostConquestTransfer(state, { armies: Number(body.armies) })
      } else if (action === 'fortify') {
        const move = body as unknown as FortifyRequest
        const decision = canFortify(state, move)
        requireMove(decision.allowed, decision.reason)
        state = applyFortify(state, move)
      } else if (action === 'return-to-attack') {
        const decision = canReturnToAttack(state)
        requireMove(decision.allowed, decision.reason)
        state = returnToAttack(state)
      } else if (action === 'turn-in-cards') {
        requireMove(state.phase === 'placement' && Array.isArray(body.cardIds), 'Choose a card set during placement')
        const cards = (state.cards?.['1'] ?? []).filter(card => (body.cardIds as string[]).includes(card.id))
        requireMove(isValidSet(cards), 'Choose a valid set of three cards')
        state = applyCardTurnIn(state, '1', cards)
      } else return null
      changed()
      return { body: game() }
    } catch (error) {
      if (error instanceof InvalidMove) return { status: 400, body: { message: error.message } }
      throw error
    }
  }
  return { respond, snapshot, user }
}
